/* eslint-disable @typescript-eslint/no-explicit-any */
import { useTranslation } from "react-i18next";

import { useCrudClickToEdit } from "@ryde/redux";
import {
  HANDLING_COLUMN_NAME,
  IColumnDef,
  IColumnDefWithId,
  ID_COLUMN_NAME,
  ID_EMPTY_COLUMN_NAME,
  IExtendableDto,
  IRydeColumnHelper,
  SETTINGS_COLUMN_NAME,
} from "@ryde/shared";

import {
  CellMultiIconGeneric,
  GenericSortableCell,
  IdColumnEditGeneric,
  LastHeadCellGeneric,
  MultiIconHeader,
} from "../../cells";

export const idEmptyColumnObj = <T extends IExtendableDto>(): IColumnDefWithId<
  T,
  any
> => ({
  id: ID_EMPTY_COLUMN_NAME,
  header: () => "",
  cell: () => "",
  meta: {
    fixed: true,
  },
});

export const handlingColumnObj = <T extends IExtendableDto>(): IColumnDefWithId<
  T,
  any
> => ({
  id: HANDLING_COLUMN_NAME,
  header: ({ table }) => (
    <MultiIconHeader dataIds={table.options.data?.map(x => x.id)} />
  ),
  cell: info => <CellMultiIconGeneric {...info.row} />,

  meta: {
    columnWidth: 70,
    orderable: false,
    fixed: true,
  },
});

export const lastColObj = <T extends IExtendableDto>(): IColumnDefWithId<
  T,
  any
> => ({
  id: SETTINGS_COLUMN_NAME,
  header: () => <LastHeadCellGeneric />,
  meta: {
    orderable: false,
    last: true,
  },
});

export const useIdColumnObj = <T extends IExtendableDto>(): IColumnDefWithId<
  T,
  any
> => {
  const { handleCrudClickToEdit } = useCrudClickToEdit();

  return {
    id: ID_COLUMN_NAME,
    header: () => <GenericIDColumn />,
    cell: ({ getValue }) => {
      const id = getValue();
      return (
        <IdColumnEditGeneric
          id={id}
          key={id}
          onEditLinkClick={handleCrudClickToEdit}
        />
      );
    },
    meta: {
      displayName: "Id",
      fixed: true,
    },
  };
};

export const useFixedColumnsDynamic = <T extends IExtendableDto>(
  genericColumnHelper: IRydeColumnHelper<T>,
): {
  fixedColumns: IColumnDef<T, any>[];
} => {
  const idColumnObj = useIdColumnObj<T>();

  const fixedColumns: IColumnDef<T, any>[] = [
    genericColumnHelper.display(idEmptyColumnObj()),
    genericColumnHelper.display(handlingColumnObj()),
    genericColumnHelper.accessor((x: any) => x["id"], idColumnObj),
  ];

  return { fixedColumns };
};

const GenericIDColumn = (): JSX.Element => {
  const { t } = useTranslation();
  return (
    <GenericSortableCell groupingDisabled={true} fieldNameResolved={"id"}>
      {t("general.ID")}
    </GenericSortableCell>
  );
};
